import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import { FormattedMessage, injectIntl } from 'react-intl'
import { AuditOutlined, EditOutlined } from '@ant-design/icons'
import { formatTimestamp, truncate } from '../helpers/utils'
import { navigateToVehicleTest } from '../api/vehicle-tests-api'
import { VEHICLE_TEST_ORGANISATION_TYPE } from '../constants'
import { Table, Typography, Button, Flex, Tooltip } from 'antd'
import { navigateToIncidentMgmtPage } from '../IncidentMgmt/IncidentMgmt'
import { fetchIssue, getIssueProblemLogs } from '../api/issues'

const { Title, Text } = Typography

const useVehicleTests = (testZones, timeAfterTest) => {
  const testZoneParams = testZones.map((zone) => ['test_zone', zone])
  const params = new URLSearchParams([
    ...testZoneParams,
    ['age[lte]', timeAfterTest.value],
    ['organisation_type', VEHICLE_TEST_ORGANISATION_TYPE.tester],
  ])

  return useQuery(
    ['vehicle-tests', testZones, timeAfterTest.value],
    async () => {
      const result = await axios.get('/tracer/vehicles/tests/', { params })
      if (
        result.data &&
        result.data.data &&
        result.data.data instanceof Array &&
        result.data.data.length > 0 &&
        result.data.data[0].sequence_number
      ) {
        return result.data.data.sort((a, b) => {
          return a.sequence_number.localeCompare(b.sequence_number)
        })
      }
      return result.data.data
    },
    {
      refetchInterval: 5000,
      enabled: !!testZones,
    },
  )
}

const AssociateIssueButton = ({ vehicleTest }) => {
  const history = useHistory()
  const [isLoadingIssue, setIsLoadingIssue] = useState(false)

  if (vehicleTest.issue_id) {
    // Same "open in edit mode" pattern as IssueList.js's EditOrAttachToIssueButton --
    // navigateToIncidentMgmtPage needs a fault with the full issue attached, but all
    // we have here is the issue id, so fetch both before navigating.
    const openIssueForEditing = async () => {
      setIsLoadingIssue(true)
      const issueId = vehicleTest.issue_id
      try {
        const [issue, problemLogsByIssueId] = await Promise.all([
          fetchIssue(issueId),
          getIssueProblemLogs(issueId),
        ])
        const fault = problemLogsByIssueId[issueId]?.[0]
        if (!fault) return
        fault.attachedIssue = issue
        navigateToIncidentMgmtPage(fault)
      } catch (err) {
        // the axios interceptor already surfaces a notification banner for
        // this -- just log it and make sure the button doesn't stay stuck
        // in its loading state.
        console.error('Failed to open issue for editing:', err)
      } finally {
        setIsLoadingIssue(false)
      }
    }
    return (
      <Tooltip title={truncate(vehicleTest.issue_description)}>
        <Button
          icon={<AuditOutlined />}
          onClick={openIssueForEditing}
          loading={isLoadingIssue}
        >
          {vehicleTest.issue_id}
        </Button>
      </Tooltip>
    )
  }
  return (
    <Button
      icon={<AuditOutlined />}
      onClick={() =>
        history.push(`/production-line-dashboard/associate/${vehicleTest.id}`, {
          vehicleTest,
        })
      }
    >
      <FormattedMessage id="productionLineDashboard.dashboardResults.associate" />
    </Button>
  )
}

const RepairNoteCell = ({ vehicleTest }) => {
  const history = useHistory()
  if (!vehicleTest.repair_note_problem_log_id) return <span>—</span>
  return (
    <Tooltip title={vehicleTest.repair_note_annotation}>
      <Text
        type="danger"
        style={{ cursor: 'pointer' }}
        onClick={() =>
          history.push(
            `/production-line-dashboard/repair-note/vehicle-test/${vehicleTest.id}`,
            {
              vehicleTest,
              existingProblemLogId: vehicleTest.repair_note_problem_log_id,
              existingAnnotation: vehicleTest.repair_note_annotation,
              existingDtcs: vehicleTest.repair_note_dtcs ?? [],
            },
          )
        }
      >
        <EditOutlined />{' '}
        <FormattedMessage id="productionLineDashboard.dashboardResults.pending" />
      </Text>
    </Tooltip>
  )
}

const ViewVehicleTestButton = ({ vehicleTestId }) => {
  return (
    <Button
      type="primary"
      onClick={() =>
        navigateToVehicleTest({
          vehicleTestId: vehicleTestId,
          newTab: false,
          navigatedFrom: 'production-line-dashboard',
          online: true,
        })
      }
    >
      <FormattedMessage id="productionLineDashboard.dashboardResults.view" />
    </Button>
  )
}

function DashboardResults({ intl, searchTerm, allTestZones }) {
  const { testZones, timeAfterTest, plant } = searchTerm
  const { status, data, isFetching } = useVehicleTests(testZones, timeAfterTest)
  const [allTestZoneDescription, setAllTestZoneDescription] = useState([])

  useEffect(() => {
    if (
      allTestZones &&
      plant &&
      plant.value &&
      testZones &&
      testZones.length > 0
    ) {
      setTestLabelsByPlant(allTestZones, plant.value, new Set(testZones))
    }
  }, [allTestZones, plant, testZones])

  const setTestLabelsByPlant = (testZones, plantName, selectedTestZones) => {
    const testZonesFilters = testZones
      .filter(
        (zone) =>
          zone.plant.name === plantName && selectedTestZones.has(zone.id),
      )
      .map((zone) => `${zone.specifier} - ${zone.description}`)
    setAllTestZoneDescription(testZonesFilters)
  }

  const dateTimeFormat = intl.formatMessage({ id: 'format.dayAndTime' })

  const columns = React.useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'productionLineDashboard.resultsTable.vin',
        }),
        dataIndex: 'vin',
      },
      {
        title: intl.formatMessage({
          id: 'productionLineDashboard.resultsTable.sequence',
        }),
        dataIndex: 'sequence_number',
      },
      {
        title: intl.formatMessage({
          id: 'productionLineDashboard.resultsTable.date',
        }),
        render: (record) =>
          formatTimestamp(dateTimeFormat, record.test_timestamp),
      },
      {
        title: intl.formatMessage({
          id: 'productionLineDashboard.resultsTable.testZone',
        }),
        render: (record) =>
          record.test_zone.description
            ? record.test_zone.description
            : record.test_zone.specifier,
      },
      {
        title: intl.formatMessage({
          id: 'productionLineDashboard.resultsTable.issueId',
        }),
        render: (record) => <AssociateIssueButton vehicleTest={record} />,
      },
      {
        title: intl.formatMessage({
          id: 'productionLineDashboard.resultsTable.repairNote',
        }),
        render: (record) => <RepairNoteCell vehicleTest={record} />,
      },
      {
        title: '',
        dataIndex: 'id',
        render: (record) => <ViewVehicleTestButton vehicleTestId={record} />,
      },
    ],
    [],
  )

  if (testZones.length === 0) {
    return (
      <Title level={4}>
        <FormattedMessage id="productionLineDashboard.dashboardResults.helpMessage" />
      </Title>
    )
  }

  if (status === 'error') {
    return (
      <Flex vertical align="center">
        <Title level={4}>
          <FormattedMessage id="productionLineDashboard.dashboardResults.helpMessageError" />
        </Title>
        {isFetching ? (
          <div>
            <Title level={4}>
              <FormattedMessage id="productionLineDashboard.dashboardResults.refreshing" />
            </Title>
          </div>
        ) : null}
      </Flex>
    )
  }

  if (data && data.length > 0) {
    return (
      <Table
        columns={columns}
        dataSource={data}
        id="production-line-results"
        rowKey="id"
        expandable={{
          expandedRowRender: (record) => {
            const dtcs = record.dtcs || []
            if (dtcs.length === 0) return null
            return (
              <span>
                <strong>{dtcs.length} DTCs: </strong>
                {dtcs.map((dtc, i) => (
                  <span key={`${dtc.code}-${i}`}>
                    {i > 0 && ', '}
                    <strong>{dtc.code}</strong>
                    {dtc.description && ` – ${dtc.description}`}
                  </span>
                ))}
              </span>
            )
          },
          rowExpandable: (record) => (record.dtcs || []).length > 0,
        }}
        pagination={{
          pageSize: 9,
          hideOnSinglePage: true,
        }}
      />
    )
  }

  return (
    <Flex vertical align="center">
      {allTestZones && (
        <Title level={4}>
          <FormattedMessage id="productionLineDashboard.dashboardResults.helpMessageNoTestZone" />{' '}
          {allTestZoneDescription.map((zone) => `"${zone}"`).join(', ')}
        </Title>
      )}
      {isFetching ? (
        <Title level={4}>
          <FormattedMessage id="productionLineDashboard.dashboardResults.refreshing" />
        </Title>
      ) : null}
    </Flex>
  )
}

export default injectIntl(DashboardResults)
